import { useQuery } from "@tanstack/react-query";

import { getRecentStudyLogs, getStudyStreak } from "@/api/stats";
import { useAuthStore } from "@/store/authStore";

export type StudyStreakData = {
  /** 연속 학습일 수 */
  streak: number;
  /** 최근 2주 학습 여부 (월요일 시작, 오래된 날 → 최근 날 순서) */
  recentActivity: boolean[];
  /** 표시 기간 중 학습한 일수 */
  activeDays: number;
};

/** 로컬 타임존 기준 YYYY-MM-DD */
function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

/** 이번 주 월요일로부터 2주 전 월요일까지의 범위를 계산 */
function getTwoWeekRange(): { start: Date; days: number } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 오늘의 요일 (월=0 ~ 일=6 으로 변환, JS getDay()는 일=0)
  const jsDay = today.getDay();
  const daysSinceMonday = jsDay === 0 ? 6 : jsDay - 1;

  // 이번 주 월요일
  const thisMonday = new Date(today);
  thisMonday.setDate(today.getDate() - daysSinceMonday);

  // 2주 전 월요일
  const start = new Date(thisMonday);
  start.setDate(thisMonday.getDate() - 7);

  // start(2주 전 월요일) ~ today 까지의 일수
  const diffMs = today.getTime() - start.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;

  return { start, days };
}

/** 학습 스트릭 + 최근 2주(월요일 기준) 활동 조회 */
export function useStudyStreakQuery() {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery({
    queryKey: ["studyStreak", userId],
    queryFn: async (): Promise<StudyStreakData> => {
      const { start, days } = getTwoWeekRange();

      const [streak, logs] = await Promise.all([getStudyStreak(userId!), getRecentStudyLogs(userId!, days)]);

      const logDates = new Set(logs.map(l => l.study_date));

      const recentActivity = Array.from({ length: days }, (_, i) => {
        const date = new Date(start);
        date.setDate(start.getDate() + i);

        return logDates.has(toLocalDateString(date));
      });

      return {
        streak,
        recentActivity,
        activeDays: recentActivity.filter(Boolean).length,
      };
    },
    enabled: Boolean(userId),
  });
}
